import { useAppDispatch, useAppSelector } from './useReduxHooks';
import {
  deviceActions,
  selectDrugUnsavedChangesMessage,
  selectIsEditingStill,
} from 'store/slices/device';
import { useTranslation } from 'react-i18next';
import { fireAlert } from 'components/UI/SwalAlert/SwalAlert';

/**
 * @description Custom React hook that prompts the user to confirm an action when there are unsaved changes.
 * This hook handles exceptional cases where the standard usePrompt hook cannot be triggered, such as:
 * a) links with onClick logic (no direct navigation), or
 * b) buttons/divs with onClick events that destroy the component where we want to prevent unsaved changes
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @returns {Function} handleBlock function that accepts a callback to execute after user confirmation
 *
 * @example
 * const handleBlock = useBlockerExceptionalCases()
 */
const useBlockerExceptionalCases = () => {
  const { t } = useTranslation();
  const isEditingStill = useAppSelector(selectIsEditingStill);
  const drugUnsavedChangesMessage = useAppSelector(
    selectDrugUnsavedChangesMessage,
  );
  const dispatch = useAppDispatch();

  /**
   * @description If form has unsaved changes (isDirty), user will be prompted to confirm the action.
   * The dirty state is determined by the Redux store slice
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @param {Function} cb - Callback function to execute if user confirms or if there are no unsaved changes
   *
   * @returns {Promise<ReturnType<T> | undefined>} undefined if user cancels, or the return value of the callback function
   *
   * @example
   * <button onClick={async () => await handleBlock(fnIWantToExecuteIfUserConfirms)}/>
   *
   * @example
   * <button onClick={async () => {
   *         if (!(await handleBlock(() => true))) return;
   *         restOfMyLogic()
   * }} />
   */
  const handleBlock = <T extends () => any>(
    cb: T,
  ): Promise<ReturnType<T> | undefined> => {
    if (isEditingStill) {
      //TODO: check what do we do with that blockDoubleModal and add that info here
      //when handleBlock is called within a fn that alters the url, the original useBlocker hook will be triggered and modal will appear twice. This action will prevent that
      dispatch(deviceActions.updateBlockDoubleModal(true));

      return new Promise((resolve) => {
        fireAlert({
          title: t('messages.unsaved_changes_title'),
          html: t(drugUnsavedChangesMessage || 'messages.unsaved_changes'),
        }).then((result) => {
          if (result.isDismissed || result.isDenied) {
            resolve(undefined);
          } else {
            resolve(cb());
          }

          setTimeout(() => {
            dispatch(deviceActions.updateBlockDoubleModal(false));
          }, 100);
        });
      });
    } else {
      return cb();
    }
  };

  return handleBlock;
};

export default useBlockerExceptionalCases;
